// T29 + T30 — NewProjectModal redesign.
//
// Default flow: admin configureert modules + partner + netwerk → modal
// genereert een /signup-deeplink. Admin deelt die met de grantee, die
// vervolgens zelf de onboarding doorloopt (eigen account, eigen org-info,
// betaling, etc.).
//
// Demo bypass: toggle "Direct aanmaken (demo)" creëert het project meteen
// via createProjectAccount — handig voor sandbox-omgevingen waar je geen
// echte signup-flow wilt doorlopen. In demo-mode komen ook project-naam,
// logo, website en centralEmail-velden terug.
//
// Spec: docs/features/onboarding-flow.md sectie "Netwerk- en partner-context"
// + Phase H (T29 + T30).

import { useEffect, useMemo, useState } from 'react'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'

import Modal from '../common/Modal'
import SaveFile from '../../hooks/SaveFile'
import UploadProgress from '../common/UploadProgress'
import MODULES from '../../helpers/subscriptionHelpers/moduleDefinitions'
import buildSignupDeeplink from '../../helpers/onboardingHelpers/buildSignupDeeplink'
import applyPartnerContext from '../../helpers/onboardingHelpers/applyPartnerContext'
import useAvailableNetworks from '../../hooks/useAvailableNetworks'
import usePartner from '../../hooks/usePartner'
import useOwnPartner from '../../hooks/useOwnPartner'

const MODULE_SLUGS = ['impactMeten', 'personas', 'housevisits', 'portfolio']
const SUPER_ADMIN = '123423HJKHVKH'

const NewProjectModal = ({
    newProjectModal,
    setNewProjectModal,
    setProjectName,
    setLogo,
    setWebsite,
    logo,
    addProject,
    loading,
    modules,
    setModules,
    centralEmail,
    setCentralEmail,
    partnerSlug,
    setPartnerSlug,
    networkCompagnyID,
    setNetworkCompagnyID,
    demoMode,
    setDemoMode,
    parentCompagnyId,
}) => {

    const [logoProgress, setLogoProgress] = useState(0)
    const [logoError, setLogoError] = useState(null)
    const [logoMeta, setLogoMeta] = useState(null)
    const [copied, setCopied] = useState(false)

    // Netwerk-dropdown is alleen voor SUPER_ADMIN-tenant; sla de Firestore-
    // listener over voor alle andere portfolio-admins om kruistenant-reads te
    // voorkomen.
    const { networks } = useAvailableNetworks(parentCompagnyId, {
        enabled: parentCompagnyId === SUPER_ADMIN,
    })
    const { ownPartner } = useOwnPartner()

    // Auto-detect: als deze portfolio-tenant zelf óók een partner is
    // (Partners-record met `networkCompagnyID == client`), pre-selecteer dan
    // die partner-slug en verberg de handmatige dropdown verderop.
    useEffect(() => {
        if (ownPartner?.slug) setPartnerSlug(ownPartner.slug)
    }, [ownPartner, setPartnerSlug])

    // Partner-context: zelfde mechanisme als de onboarding-flow. Bij selectie
    // van een partner overschrijven we modules met de partner's pakket en
    // locken we de checkboxes voor modules die de partner dicteert.
    const { partner } = usePartner(partnerSlug)
    // mode='admin': partner-admin maakt sub-projects en mag dus modules
    // bijschakelen die buiten het partner-pakket vallen. Strict-mode geldt
    // alleen voor de grantee-onboarding-flow.
    const { initialModules, lockedModules } = useMemo(
        () => applyPartnerContext({ partner, deeplink: {}, mode: 'admin' }),
        [partner]
    )

    useEffect(() => {
        if (!partner) return
        setModules(initialModules)
    }, [partner, initialModules, setModules])

    const isLocked = (slug) => lockedModules.includes(slug)

    const logoHandler = (e) => {
        setLogoError(null)
        SaveFile(e, setLogo, setLogoMeta, setLogoProgress, (err) => setLogoError(err?.message || 'Upload mislukt'))
    }

    const toggleModule = (slug) => {
        if (isLocked(slug)) return
        setModules((prev) => ({ ...prev, [slug]: !prev?.[slug] }))
    }

    const deeplinkUrl = useMemo(() => buildSignupDeeplink({
        origin: window.location.origin,
        modules,
        partnerSlug,
        networkCompagnyID,
    }), [modules, partnerSlug, networkCompagnyID])

    const copyDeeplink = async () => {
        try {
            await navigator.clipboard.writeText(deeplinkUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.warn('Clipboard copy failed', err)
        }
    }

    const content = (
        <div>

            <div className='add-new-project-modal-input-container'>
                <label><b>Modules</b></label>
                <div className='add-new-project-modal-modules-grid'>
                    {MODULE_SLUGS.map((slug) => {
                        const mod = MODULES[slug]
                        const checked = !!modules?.[slug]
                        const locked = isLocked(slug)
                        const freeForPartner = partner?.freeIncludedModules?.includes(slug)
                        return (
                            <label
                                key={slug}
                                className={`add-new-project-modal-module-row ${checked ? 'checked' : ''} ${locked ? 'locked' : ''}`}
                            >
                                <input
                                    type='checkbox'
                                    checked={checked}
                                    disabled={locked}
                                    onChange={() => toggleModule(slug)}
                                />
                                <span>{mod.name}</span>
                                {locked && (
                                    <span className='add-new-project-modal-module-locked-tag'>
                                        {checked ? 'Inbegrepen bij partner' : 'Niet beschikbaar bij partner'}
                                    </span>
                                )}
                                <span className='add-new-project-modal-module-price'>
                                    {freeForPartner ? 'Gratis bij partner' : `€${mod.price}/mnd`}
                                </span>
                            </label>
                        )
                    })}
                </div>
            </div>

            {parentCompagnyId === SUPER_ADMIN && (
                <div className='add-new-project-modal-input-container'>
                    <label><b>Netwerk (optioneel)</b></label>
                    <select
                        value={networkCompagnyID || ''}
                        onChange={(e) => setNetworkCompagnyID(e.target.value || null)}
                    >
                        <option value=''>— Geen netwerk —</option>
                        {networks.map((n) => (
                            <option key={n.CompagnyID} value={n.CompagnyID}>
                                {n.CommunityName || n.CompagnyID}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className='add-new-project-modal-toggle-row'>
                <label className='add-new-project-modal-toggle'>
                    <input
                        type='checkbox'
                        checked={!!demoMode}
                        onChange={(e) => setDemoMode(e.target.checked)}
                    />
                    <span><b>Direct aanmaken (intern project)</b> — sla onboarding-flow over en maak het project nu aan</span>
                </label>
            </div>

            {!demoMode && (
                <div className='add-new-project-modal-deeplink-container'>
                    <label><b>Signup-deeplink</b></label>
                    <p className='add-new-project-modal-deeplink-hint'>
                        Stuur deze link naar de organisatie. Zij doorlopen daarmee zelf
                        de onboarding met de bovenstaande modules en partner-context al ingevuld.
                    </p>
                    <div className='add-new-project-modal-deeplink-row'>
                        <input
                            type='text'
                            readOnly
                            value={deeplinkUrl}
                            className='add-new-project-modal-deeplink-input'
                        />
                        <button
                            type='button'
                            className='add-new-project-modal-deeplink-copy'
                            onClick={copyDeeplink}
                            aria-label='Kopiëer deeplink'
                        >
                            {copied
                                ? <CheckCircleOutlinedIcon style={{ color: '#2e7d32' }} />
                                : <ContentCopyOutlinedIcon />
                            }
                        </button>
                    </div>
                </div>
            )}

            {demoMode && (
                <>
                    <div className='add-new-project-modal-input-container'>
                        <label><b>Projectnaam</b></label>
                        <input type='text' onChange={(e) => setProjectName(e.target.value)} />
                    </div>

                    <div className='add-new-project-modal-input-container'>
                        <label><b>Logo</b></label>
                        <img src={logo} alt='' id='add-new-project-modal-image' />
                        <input
                            type='file'
                            onChange={logoHandler}
                            disabled={logoProgress > 0 && logoProgress < 100}
                        />
                        <UploadProgress
                            progress={logoProgress}
                            error={logoError}
                            fileName={logoMeta?.name}
                        />
                    </div>

                    <div className='add-new-project-modal-input-container'>
                        <label><b>Website</b></label>
                        <input type='text' placeholder='Schrijf hier de website' onChange={(e) => setWebsite(e.target.value)} />
                    </div>

                    <div className='add-new-project-modal-input-container'>
                        <label><b>Centraal e-mailadres</b></label>
                        <input
                            type='email'
                            placeholder='info@organisatie.nl'
                            value={centralEmail || ''}
                            onChange={(e) => setCentralEmail(e.target.value)}
                        />
                    </div>
                </>
            )}
        </div>
    )

    // In demo-mode: confirm = aanmaken project. Daarbuiten: confirm = sluiten
    // (deeplink zelf wordt via copy-knop gedeeld; modal-confirm is dan de
    // "klaar"-actie).
    const confirmAction = demoMode ? addProject : () => setNewProjectModal(false)
    const confirmLabel = demoMode
        ? (loading ? 'Bezig...' : 'Project aanmaken')
        : 'Klaar'

    return (
        <Modal
            modalOpen={newProjectModal}
            setModalOpen={setNewProjectModal}
            confirm={confirmAction}
            pageHeader='Nieuw project toevoegen'
            content={content}
            confirmLabel={confirmLabel}
            cancelLabel='Annuleren'
        />
    )

}

export default NewProjectModal
